import { describirEntidad } from '../experiencia/auditoria';
import { useCallback, useEffect, useState } from 'react';
import { Server, Loader2, AlertTriangle, Pencil, Check } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAvisos } from '../lib/contextoAvisos';
import NotaExplicativa from '../ui/NotaExplicativa';
import ExportarTodo from '../ui/ExportarTodo';
import CatalogoGeografico from '../importador/CatalogoGeografico';

// CÓMO ESTÁ ESTE SERVIDOR, para su dueño y sin llamar a nadie.
//
// ===================================================================
// NUNCA UN ICONO VERDE QUE MIENTE
// ===================================================================
//
// Es la frase del §7 de DIRECTRIZ-NODOS.md y es la única regla de diseño
// de esta pantalla. Un icono que dice CONECTADO puede estar mintiendo;
// «hace 6 horas» no puede.
//
// Por eso aquí no hay una luz verde: hay, para cada tarea, cuándo fue la
// última vez y cuánto hace de eso. Y si una lleva demasiado callada, se
// marca aunque su última ejecución terminara bien — porque un «todo
// bien» de hace tres días no dice que hoy esté bien, dice que nadie lo
// ha comprobado.
//
// El estado de estas tareas lo sabía TEGO desde hace bloques, y solo lo
// veía el panel del proveedor. Este es el hueco que la fase N-0 cierra.

const TONO = {
  OK: 'text-green-700',
  ATENCION: 'text-amber-700',
  SOLO_LOCAL: 'text-amber-700',
  ATRASADO: 'text-amber-700',
  SIN_DATOS: 'text-gray-500',
  FALLO: 'text-red-700',
  AVERIA: 'text-red-700',
};

const NOMBRE = {
  OK: 'Al día',
  ATENCION: 'Revísalo',
  SOLO_LOCAL: 'Sin configurar',
  ATRASADO: 'Lleva demasiado sin correr',
  SIN_DATOS: 'Nunca se ha ejecutado',
  FALLO: 'Falló',
  AVERIA: 'Falló',
};

export default function EsteServidor() {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [editando, setEditando] = useState(false);
  const avisos = useAvisos();

  const cargar = useCallback(
    () => api.get('/nodo').then(setD).catch((e) => setError(e.message)),
    [],
  );
  useEffect(() => { cargar(); }, [cargar]);

  if (error) {
    return (
      <div className="m-4 flex gap-2 rounded-xl border border-red-200 bg-red-50
                      p-3 text-sm text-red-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{error}</span>
      </div>
    );
  }
  if (!d) {
    return (
      <p className="flex items-center gap-2 p-4 text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Mirando cómo está este servidor…
      </p>
    );
  }

  const grave = d.veredicto === 'FALLO';
  const atencion = d.veredicto === 'ATENCION';

  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Este servidor</h1>
        <p className="text-gray-600">
          Cómo está la máquina en la que corre TEGO: sus copias, su
          espacio y su versión. No hace falta llamar a nadie para verlo.
        </p>
      </header>

      {/* LA LICENCIA VA ANTES QUE NADA cuando hay algo que decir. Es lo
          único de esta pantalla que puede acabar impidiendo trabajar, y
          quien se lo encuentre tiene que poder enterarse de por qué sin
          llamar a nadie. */}
      {d.licencia?.mensaje && (
        <NotaExplicativa
          titulo={d.licencia.solo_lectura
            ? 'TEGO está en modo consulta'
            : 'Tu suscripción'}>
          {d.licencia.mensaje}
          {d.licencia.solo_lectura && (
            <>
              {' '}
              <strong>
                Tus datos siguen aquí, completos, y los puedes exportar
                cuando quieras.
              </strong>{' '}
              No se ha borrado nada y no se va a borrar nada.
            </>
          )}
        </NotaExplicativa>
      )}

      {/* EL VEREDICTO. Quien abre esto no viene a leerlo entero: viene a
          ver si hay algo que atender. */}
      {(grave || atencion) && (
        <NotaExplicativa
          titulo={grave ? 'Hay algo roto' : 'Hay algo que conviene mirar'}>
          {d.resumen}
        </NotaExplicativa>
      )}

      <ExportarTodo />

      {/* EL CATÁLOGO GEOGRÁFICO, con su versión y su fuente · MAESTRA
          I.1.8 (carril importador, montado aquí por el integrador el
          14 de septiembre de 2026). */}
      <CatalogoGeografico />

      {/* ------------------------------------------------ quién es ---- */}
      <section className="rounded-xl border border-gray-200 p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-gray-100 p-2">
            <Server className="h-5 w-5 text-gray-500" aria-hidden="true" />
          </div>
          <div className="flex-1">
            {editando ? (
              <Renombrar
                actual={d.nodo.nombre}
                onListo={(m) => { setEditando(false); avisos.exito(m); cargar(); }}
                onCancelar={() => setEditando(false)}
              />
            ) : (
              <>
                <p className="flex items-center gap-2 text-lg font-semibold
                              text-gray-900">
                  {d.nodo.nombre}
                  <button type="button" onClick={() => setEditando(true)}
                          className="inline-flex items-center gap-1 text-sm
                                     font-semibold text-[var(--color-marca)]">
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                    Cambiar el nombre
                  </button>
                </p>
                <p className="text-sm text-gray-600">
                  Versión <strong>{d.version}</strong>
                  {d.nodo.desde?.hace && <> · instalado {d.nodo.desde.hace}</>}
                </p>
                {/* El identificador se enseña pero no se puede cambiar: es
                    lo que hace que esta máquina sea esta y no otra. */}
                <p className="mt-1 font-mono text-[11px] text-gray-600">
                  {d.nodo.id}
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------ la máquina -- */}
      <section className="grid gap-3 sm:grid-cols-2">
        <Dato titulo="Espacio en disco" estado={d.disco.estado}
              detalle={d.disco.detalle}
              valor={d.disco.libre_gb != null
                ? `${d.disco.libre_gb} GB libres` : '—'} />
        <Dato titulo="Tamaño de la base"
              estado={d.base.estado}
              valor={d.base.tamano_gb != null
                ? `${d.base.tamano_gb} GB` : 'no se pudo medir'}
              detalle={d.esquema.detalle
                || (d.esquema.ultima
                  ? `${d.esquema.migraciones} cambios de esquema aplicados. Último: ${d.esquema.ultima}`
                  : null)} />
      </section>

      {/* ------------------------------------------------ las copias -- */}
      <section className="rounded-xl border border-gray-200">
        <div className="border-b border-gray-100 px-4 py-3">
          <h2 className="font-semibold text-gray-900">
            Las copias y los vigías
          </h2>
          <p className="text-sm text-gray-600">
            Cada uno dice <strong>cuándo fue la última vez</strong>, no si
            está bien. Una tarea que lleva demasiado callada se marca
            aunque su última vuelta terminara sin errores: el silencio no
            avisa solo.
          </p>
        </div>
        <ul className="divide-y divide-gray-100">
          {(d.tareas || []).map((t) => (
            <li key={t.tarea} className="flex flex-wrap items-baseline
                                         gap-x-3 px-4 py-2 text-sm">
              <span className="font-medium text-gray-900">{t.titulo}</span>
              <span className={TONO[t.estado] || 'text-gray-500'}>
                {NOMBRE[t.estado] || t.estado}
              </span>
              <span className="ml-auto text-gray-500">
                {t.horas == null
                  ? 'nunca'
                  : t.horas < 48
                    ? `hace ${Math.round(t.horas)} h`
                    : `hace ${Math.round(t.horas / 24)} días`}
              </span>
              {t.detalle && (
                <span className="w-full text-xs text-gray-600">{enPalabras(t.detalle)}</span>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* HAY VERSIÓN NUEVA. Va con su «se puede ahora» pegado: enseñar
          que hay actualización sin decir que no se puede porque hay una
          caja abierta deja a alguien pulsando un botón que no hace
          nada. */}
      {d.actualizacion?.hay_version_nueva && (
        <section className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <h2 className="font-semibold text-gray-900">
            Hay una versión nueva: {d.actualizacion.version}
          </h2>
          {d.actualizacion.notas && (
            <p className="mt-1 text-sm text-gray-700">
              {d.actualizacion.notas}
            </p>
          )}
          <p className="mt-2 text-sm text-gray-700">
            {d.actualizacion.se_puede_ahora ? (
              <>
                Se puede instalar ahora. Se toma un respaldo antes y, si
                algo saliera mal, se vuelve sola a la versión de ahora.
                Ejecuta en el servidor:{' '}
                <code className="rounded bg-white px-1 py-0.5 text-xs">
                  ./scripts/actualizar-nodo.sh
                </code>
              </>
            ) : (
              <>
                <strong>Ahora no se puede.</strong>{' '}
                {d.actualizacion.por_que_no}
              </>
            )}
          </p>
          <p className="mt-2 text-xs text-gray-600">
            {d.actualizacion.modo === 'AUTOMATICA'
              ? 'Este servidor se actualiza solo, de madrugada y solo si no hay ninguna caja abierta.'
              : 'Este servidor NO se actualiza solo: la instala alguien cuando quiera.'}
          </p>
        </section>
      )}

      <TuPlan cuenta={d.cuenta} uso={d.uso} />
      <TuActividad actividad={d.actividad} />
      <ElRespaldo />
      <LoQueSeEnvia />
      <QueSube />
    </div>
  );
}

// ==================================================================
// TU PLAN · qué incluye, cuánto usas, y qué pasa con lo que sobra
// ==================================================================
//
// Fase F. Esta sección existe porque desde la Fase D los límites PEGAN:
// se puede chocar contra un tope y hasta hoy el único sitio donde se
// veía el número era el mensaje de error. Quien choca tiene derecho a
// mirar la cuenta ANTES de chocar.
//
// TRES REGLAS DE DISEÑO, Y LAS TRES SON DEL DUEÑO:
//
//   1. **Lo que ya existe por encima del límite no se toca.** Así que
//      «por encima del plan» se pinta en ÁMBAR y nunca en rojo, y la
//      frase dice lo primero que hay que decir: que no le pasa nada.
//      Un rojo aquí haría llamar por teléfono a alguien que no tiene
//      ningún problema.
//   2. **Nunca un icono verde que miente.** Cada número lleva cuándo se
//      supo. Un tope leído hace tres semanas no es el tope de hoy.
//   3. **Estado vacío útil.** La mayoría de las instalaciones no están
//      dadas de alta en ningún plan, y eso NO es un error: hay que
//      decir qué significa en vez de dejar la sección en blanco.
function TuPlan({ cuenta, uso }) {
  const topes = (uso || []).filter((u) => u.tope != null);
  const sobrando = topes.filter((u) => u.por_encima);
  const sinNoticias = !cuenta?.estado && topes.length === 0;

  return (
    <section className="rounded-xl border border-gray-200">
      <div className="border-b border-gray-100 px-4 py-3">
        <h2 className="font-semibold text-gray-900">Tu plan</h2>
        <p className="text-sm text-gray-600">
          Qué incluye y cuánto llevas usado. Los topes solo impiden{' '}
          <strong>crear de más</strong>: nunca borran ni desactivan nada de
          lo que ya tienes.
        </p>
      </div>

      {sinNoticias ? (
        // ESTADO VACÍO ÚTIL: qué es esto, por qué está vacío, y qué
        // hacer. Es el estado de casi todas las instalaciones.
        <p className="px-4 py-3 text-sm text-gray-600">
          Este servidor <strong>no está dado de alta en ningún plan</strong>
          y no tiene ningún tope puesto desde fuera. Funciona
          completo. Si contrataste TEGO y esto te extraña, escribe a tu
          proveedor.
        </p>
      ) : (
        <>
          {cuenta?.estado && (
            <p className="border-b border-gray-100 px-4 py-3 text-sm">
              <span className="text-gray-600">Estado de tu cuenta: </span>
              <strong className="text-gray-900">
                {cuenta.nombre || cuenta.estado}
              </strong>
              {/* CUÁNDO SE SUPO, siempre. Un estado sin fecha no se
                  puede juzgar: «suspendida» de hace diez minutos y de
                  hace tres semanas son cosas distintas. */}
              {cuenta.leida_en && (
                <span className="text-gray-500">
                  {' '}· según lo último que se supo,{' '}
                  {new Date(cuenta.leida_en).toLocaleString('es-MX')}
                </span>
              )}
              {cuenta.mensaje && (
                <span className="mt-1 block text-gray-700">
                  {cuenta.mensaje}
                </span>
              )}
            </p>
          )}

          {sobrando.length > 0 && (
            <div className="border-b border-amber-100 bg-amber-50 px-4 py-3
                            text-sm text-gray-800">
              <p className="font-semibold">
                Tienes más de lo que incluye tu plan, y no pasa nada.
              </p>
              <p className="mt-1">
                <strong>No se ha borrado ni desactivado nada</strong>, y no
                se va a borrar. Todo sigue funcionando igual. Lo único que
                no puedes hacer es crear más, hasta que amplíes el plan.
                Escribe a tu proveedor de TEGO cuando quieras.
              </p>
            </div>
          )}

          <ul className="divide-y divide-gray-100">
            {topes.map((u) => (
              <li key={u.limite}
                  className="flex flex-wrap items-baseline gap-x-3 px-4 py-2
                             text-sm">
                <span className="font-medium text-gray-900">
                  {u.nombre || u.limite}
                </span>
                <span className={u.por_encima
                  ? 'font-semibold text-amber-700' : 'text-gray-700'}>
                  {u.usados} de {u.tope}
                </span>
                {u.por_encima && (
                  <span className="text-amber-700">por encima del plan</span>
                )}
                <span className="ml-auto text-gray-500">
                  {u.leido_en
                    ? `se supo el ${new Date(u.leido_en).toLocaleDateString('es-MX')}`
                    : 'sin fecha'}
                </span>
                {u.empresas > 1 && (
                  <span className="w-full text-xs text-gray-500">
                    Este servidor atiende a {u.empresas} empresas y el tope
                    se aplica a cada una por separado: aquí sale la que más
                    usa.
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

// ==================================================================
// TU ACTIVIDAD · y el aviso de que esto SÍ sale de aquí
// ==================================================================
//
// Fase F, y va junto a los números a propósito. El resumen de actividad
// se encendió el 16 de agosto de 2026 con permiso escrito del dueño, y
// la regla de la Fase N-1 sigue mandando: **el cliente ve literalmente
// lo que se manda**, no una descripción de lo que suele llevar.
//
// Por eso el aviso está pegado a los propios números, y no escondido en
// un pie de página: quien mira esto tiene que ver, en el mismo golpe de
// vista, que estos ocho números son los que salen de su máquina.
function TuActividad({ actividad }) {
  if (!actividad) return null;
  const f = (v) => (v ? new Date(v).toLocaleString('es-MX') : 'nunca');

  return (
    <section className="rounded-xl border border-gray-200">
      <div className="border-b border-gray-100 px-4 py-3">
        <h2 className="font-semibold text-gray-900">
          Tu actividad de los últimos {actividad.dias} días
        </h2>
        <p className="text-sm text-gray-600">
          Cuánto se ha usado TEGO en este servidor.{' '}
          {actividad.viaja ? (
            <>
              <strong>Estos ocho números salen de aquí</strong> hacia tu
              proveedor, con cada latido. Son recuentos y fechas:{' '}
              <strong>ningún nombre, ningún producto y ningún importe</strong>.
              Puedes ver el envío exacto, palabra por palabra, más abajo.
            </>
          ) : (
            <>Estos números <strong>no salen de esta máquina</strong>.</>
          )}
        </p>
      </div>
      <ul className="grid gap-x-6 gap-y-2 px-4 py-3 text-sm sm:grid-cols-2">
        <Renglon que="Movimientos de inventario" vale={actividad.movimientos}
                 cuando={`último: ${f(actividad.ultimo_movimiento)}`} />
        <Renglon que="Ventas" vale={actividad.ventas}
                 cuando={`última: ${f(actividad.ultima_venta)}`} />
        <Renglon que="Personas que entraron"
                 vale={actividad.usuarios_que_entraron}
                 cuando={`último acceso: ${f(actividad.ultimo_acceso)}`} />
        <Renglon que="Días con movimiento"
                 vale={`${actividad.dias_con_movimiento} de ${actividad.dias}`} />
      </ul>
    </section>
  );
}

function Renglon({ que, vale, cuando }) {
  return (
    <li>
      <span className="text-gray-600">{que}: </span>
      <strong className="text-gray-900">{vale}</strong>
      {cuando && (
        <span className="block text-xs text-gray-500">{cuando}</span>
      )}
    </li>
  );
}

// -------------------------------------------------- el respaldo ----
//
// LA ETIQUETA ES HONESTA, Y ES UNA DECISIÓN DEL DUEÑO:
//
//   «El respaldo cifrado NO cuenta como respaldo mientras la nube viva
//   en esta misma laptop. Que la pantalla del cliente no diga
//   "respaldado en la nube" si la nube está en su propio servidor: ahí
//   tiene que decir lo que de verdad pasa.»
//
// El motivo: una copia en la misma máquina que el original no protege de
// nada de lo que un respaldo existe para cubrir. Y una etiqueta que
// dijera «respaldado» sería PEOR que no tener la copia, porque quien lo
// cree deja de hacer las otras.
//
// Por eso aquí no hay una palomita verde: hay una frase que dice de qué
// protege y de qué no.
function ElRespaldo() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get('/nodo/respaldo-remoto').then(setD).catch(() => setD(null));
  }, []);
  if (!d) return null;

  return (
    <section className={`rounded-xl border p-4 ${
      d.protege_de_perder_la_maquina
        ? 'border-green-200 bg-green-50'
        : 'border-amber-200 bg-amber-50'}`}>
      <h2 className="font-semibold text-gray-900">{d.titulo}</h2>
      <p className="mt-1 text-sm text-gray-700">{d.dice}</p>
      <p className="mt-2 text-xs text-gray-600">{d.aviso_llave}</p>
      {d.copias.length > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          Última: <span className="font-mono">{d.copias[0].archivo}</span> ·{' '}
          {Math.round(d.copias[0].bytes / 1024 / 1024)} MB ·{' '}
          {new Date(d.copias[0].creado_en).toLocaleString('es-MX')}
        </p>
      )}
    </section>
  );
}

// ------------------------------------------------ qué sale de aquí --
//
// LITERAL, NO UN RESUMEN. Decisión del dueño, y es lo correcto por un
// motivo concreto: una descripción de lo que suele mandarse es una
// promesa sobre el código, y se rompe sin que nadie lo note el día que
// alguien añade un campo. El contenido exacto es una prueba.
//
// Se enseñan varios y no solo el último: poder comparar dos envíos es lo
// que permite ver QUÉ CAMBIÓ.
function LoQueSeEnvia() {
  const [d, setD] = useState(null);
  const [abierto, setAbierto] = useState(0);

  useEffect(() => {
    api.get('/nodo/lo-que-se-envia').then(setD).catch(() => setD(null));
  }, []);

  if (!d) return null;

  if (!d.nube) {
    return (
      <section className="rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900">Qué sale de aquí</h2>
        <p className="mt-1 text-sm text-gray-600">
          <strong>Nada.</strong> Este servidor no está conectado a ninguna
          nube y no manda información a ninguna parte. Todo lo de
          esta pantalla se lee aquí mismo.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-gray-200">
      <div className="border-b border-gray-100 px-4 py-3">
        <h2 className="font-semibold text-gray-900">Qué sale de aquí</h2>
        <p className="text-sm text-gray-600">
          Este servidor le cuenta cómo está a{' '}
          <span className="font-mono text-xs">{d.nube}</span>. Abajo está
          el <strong>contenido exacto</strong> de cada envío, tal cual
          viajó — no un resumen.
          {d.pendientes > 0 && (
            <> Hay <strong>{d.pendientes}</strong> sin mandar todavía: se
            irán cuando vuelva la conexión.</>
          )}
        </p>
      </div>
      <ul className="divide-y divide-gray-100">
        {(d.envios || []).map((e, i) => (
          <li key={e.id} className="px-4 py-2 text-sm">
            <button type="button" onClick={() => setAbierto(abierto === i ? -1 : i)}
                    className="flex w-full flex-wrap items-center gap-x-3
                               text-left">
              <span className={TONO[e.estado === 'ENVIADO' ? 'OK' : 'ATENCION']}>
                {e.estado === 'ENVIADO' ? 'Enviado' : 'Sin mandar'}
              </span>
              <span className="text-gray-600">{e.bytes} bytes</span>
              <span className="ml-auto text-gray-500">
                {new Date(e.creado_en).toLocaleString('es-MX')}
              </span>
            </button>
            {abierto === i && (
              <>
                {/* PRIMERO EN PALABRAS (EXPERIENCIA E3): qué lleva el envío,
                    contado. Y debajo, detrás de un clic y dicho como
                    técnico, el contenido TAL CUAL —sin ordenar, sin ocultar
                    campos y sin reformatear—, porque si lo tocáramos
                    dejaría de ser una prueba de lo que se mandó. */}
                <ResumenDeEnvio cuerpo={e.cuerpo} />
                <details className="mt-2 text-xs text-gray-600">
                  <summary className="cursor-pointer select-none">Ver el contenido exacto (técnico)</summary>
                  <pre className="mt-2 max-h-80 overflow-auto rounded-lg
                                  bg-gray-900 p-3 text-xs text-gray-100">
                    {e.cuerpo}
                  </pre>
                </details>
                {e.respuesta && (
                  <p className="mt-1 text-xs text-gray-600">
                    Contestó: {resumirRespuesta(e.respuesta)}
                  </p>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
      {(d.envios || []).length === 0 && (
        <p className="px-4 py-3 text-sm text-gray-600">
          Sin envíos.
        </p>
      )}
    </section>
  );
}

// ------------------------------------------------- qué puede subir --
//
// LA LISTA BLANCA ENTERA, con el motivo de cada clasificación. Junto al
// panel de arriba —que enseña el contenido exacto de cada envío— dan la
// respuesta completa a «¿qué se lleva TEGO de mi empresa?».
//
// Se enseña primero lo que SÍ sale, que es lo corto, y lo que no queda
// plegado: quien viene a revisar esto quiere ver primero la lista breve.
function QueSube() {
  const [d, setD] = useState(null);
  const [verTodo, setVerTodo] = useState(false);

  useEffect(() => {
    api.get('/nodo/que-sube').then(setD).catch(() => setD(null));
  }, []);
  if (!d) return null;

  return (
    <section className="rounded-xl border border-gray-200">
      <div className="border-b border-gray-100 px-4 py-3">
        <h2 className="font-semibold text-gray-900">
          Qué puede salir de este servidor
        </h2>
        <p className="text-sm text-gray-600">
          De {d.suben.length + (d.no_suben || []).length} tablas de datos,{' '}
          <strong>{d.suben.length}</strong> pueden salir y{' '}
          <strong>{(d.no_suben || []).length}</strong> no. Lo que no está en esta
          lista tampoco sale: la lista dice lo que SÍ puede, nunca lo que
          no.
        </p>
      </div>
      <ul className="divide-y divide-gray-100">
        {d.suben.map((t) => (
          <li key={t.tabla} className="px-4 py-2 text-sm">
            {/* EN PALABRAS (EXPERIENCIA E5): el nombre de la tabla es técnico;
                se dice qué es y el nombre exacto queda como título al pasar. */}
            <span className="text-gray-900" title={t.tabla}>{describirEntidad(t.tabla)}</span>
            <span className="ml-2 text-green-700">sale</span>
            <span className="block text-xs text-gray-500">{t.motivo}</span>
          </li>
        ))}
      </ul>
      <div className="px-4 py-2">
        <button type="button" onClick={() => setVerTodo(!verTodo)}
                className="text-sm font-semibold text-[var(--color-marca)]">
          {verTodo ? 'Ocultar' : `Ver las ${(d.no_suben || []).length} que NO salen`}
        </button>
      </div>
      {verTodo && (
        <ul className="divide-y divide-gray-100 border-t border-gray-100">
          {(d.no_suben || []).map((t) => (
            <li key={t.tabla} className="px-4 py-2 text-sm">
              <span className="text-gray-900" title={t.tabla}>{describirEntidad(t.tabla)}</span>
              <span className={`ml-2 ${t.clase === 'PROHIBIDA'
                ? 'font-semibold text-red-700' : 'text-gray-500'}`}>
                {t.clase === 'PROHIBIDA' ? 'nunca sale' : 'no sale'}
              </span>
              <span className="block text-xs text-gray-500">{t.motivo}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Dato({ titulo, valor, detalle, estado }) {
  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <p className="text-sm font-semibold text-gray-700">{titulo}</p>
      <p className={`text-lg font-semibold ${TONO[estado] || 'text-gray-900'}`}>
        {valor}
      </p>
      {detalle && <p className="mt-1 text-xs text-gray-500">{detalle}</p>}
    </div>
  );
}

function Renombrar({ actual, onListo, onCancelar }) {
  const [nombre, setNombre] = useState(actual);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const corto = nombre.trim().length < 2;

  return (
    <div className="flex flex-col gap-2">
      <label className="block text-sm">
        <span className="font-semibold text-gray-700">
          ¿Cómo llamas a este servidor?
        </span>
        <input className="entrada mt-1 w-full" value={nombre} autoFocus
               onChange={(e) => setNombre(e.target.value)}
               placeholder="Bodega Norte" />
        <span className="mt-1 block text-xs text-gray-500">
          El nombre es para ti y para quien administre la máquina. Su
          identificador no cambia nunca.
        </span>
      </label>
      {error && <p className="text-sm text-red-700">{error}</p>}
      <div className="flex items-center gap-3">
        <button
          type="button" disabled={corto || guardando}
          onClick={async () => {
            setGuardando(true); setError('');
            try {
              const r = await api.patch('/nodo', { nombre: nombre.trim() });
              onListo(r.mensaje);
            } catch (e) {
              setError(e instanceof ErrorApi ? e.message : String(e));
            } finally { setGuardando(false); }
          }}
          className="inline-flex items-center gap-1 rounded-lg
                     bg-[var(--color-marca)] px-3 py-2 font-semibold
                     text-white disabled:bg-gray-300">
          {guardando
            ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            : <Check className="h-4 w-4" aria-hidden="true" />}
          Guardar
        </button>
        <button type="button" onClick={onCancelar}
                className="font-semibold text-gray-600">
          Mejor no
        </button>
        {/* Botón apagado CON LA RAZÓN A LA VISTA. */}
        {corto && (
          <span className="text-sm text-gray-600">
            Escribe al menos dos letras.
          </span>
        )}
      </div>
    </div>
  );
}


/* Qué lleva un envío, en una frase por parte: «Actividad: 30 días, 7
   movimientos»; «Empresas: 8». Cuenta, no copia. Si el cuerpo no es
   JSON, lo dice y ya. */
function ResumenDeEnvio({ cuerpo }) {
  let datos = null;
  try { datos = JSON.parse(cuerpo); } catch { datos = null; }
  if (!datos || typeof datos !== 'object') {
    return <p className="mt-2 text-xs text-gray-600">El envío no es un texto estructurado; abajo está tal cual.</p>;
  }
  const partes = Object.entries(datos).map(([k, v]) => {
    const nombre = k.replace(/_/g, ' ');
    if (Array.isArray(v)) return `${nombre}: ${v.length} elemento(s)`;
    if (v && typeof v === 'object') {
      const n = Object.entries(v).filter(([, x]) => typeof x === 'number').slice(0, 4)
        .map(([kk, x]) => `${kk.replace(/_/g, ' ')} ${x}`).join(', ');
      return `${nombre}: ${n || `${Object.keys(v).length} dato(s)`}`;
    }
    return `${nombre}: ${String(v).slice(0, 40)}`;
  });
  return (
    <ul className="mt-2 list-disc pl-5 text-xs text-gray-700">
      {partes.slice(0, 12).map((p) => <li key={p}>{p}</li>)}
      {partes.length > 12 && <li>… y {partes.length - 12} parte(s) más</li>}
    </ul>
  );
}


/* La respuesta de la nube, en una frase: «recibido; licencia ACTIVA hasta
   2026-10-01». Si no es JSON, tal cual (recortada). */
function resumirRespuesta(texto) {
  let r = null;
  try { r = JSON.parse(texto); } catch { return String(texto).slice(0, 120); }
  if (!r || typeof r !== 'object') return String(texto).slice(0, 120);
  const partes = [];
  if (r.recibido !== undefined) partes.push(r.recibido ? 'recibido' : 'no recibido');
  if (r.licencia && typeof r.licencia === 'object') {
    partes.push(`licencia ${String(r.licencia.estado || '').toLowerCase() || 'sin estado'}${r.licencia.hasta ? ` hasta ${String(r.licencia.hasta).slice(0, 10)}` : ''}`);
  }
  if (r.mensaje) partes.push(String(r.mensaje));
  for (const [k, v] of Object.entries(r)) {
    if (['recibido', 'licencia', 'mensaje'].includes(k)) continue;
    if (v && typeof v === 'object') partes.push(`${k.replace(/_/g, ' ')}: ${Array.isArray(v) ? v.length + ' elemento(s)' : Object.keys(v).length + ' dato(s)'}`);
    else if (v !== null && v !== undefined) partes.push(`${k.replace(/_/g, ' ')}: ${String(v).slice(0, 40)}`);
  }
  return partes.join('; ') || 'sin detalle';
}


/* El detalle de una tarea, sin nombres de tabla: «corporaciones=8/8
   usuarios=25/25» (lo que deja el simulacro) se lee «Empresa 8 de 8,
   Persona 25 de 25». Lo que no tiene esa forma pasa tal cual. */
function enPalabras(texto) {
  return String(texto).replace(/\b([a-z][a-z_]+)=(\d+)\/(\d+)/g, (_, tabla, a, b) => `${describirEntidad(tabla)} ${a} de ${b}`);
}
